import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router'

import { AppLayout } from './app/AppLayout'
import { AlertsPage } from './features/alerts/AlertsPage'
import { AlertWindowsPage } from './features/alerts/AlertWindowsPage'
import { AvailabilityFormPage } from './features/availability/AvailabilityFormPage'
import { AvailabilityPage } from './features/availability/AvailabilityPage'
import { BroadcastDetailPage } from './features/broadcasts/BroadcastDetailPage'
import { BroadcastPage } from './features/broadcasts/BroadcastPage'
import { DatPastePage } from './features/loads/DatPastePage'
import { LoadFormPage } from './features/loads/LoadFormPage'
import { LoadsPage } from './features/loads/LoadsPage'
import { LoadMatchesPage } from './features/matching/LoadMatchesPage'
import { PostingMatchesPage } from './features/matching/PostingMatchesPage'
import { RateConfirmationPage } from './features/rateConfirmations/RateConfirmationPage'
import { RateConfirmationsPage } from './features/rateConfirmations/RateConfirmationsPage'
import { AuthProvider } from './features/auth/AuthProvider'
import { LoginPage } from './features/auth/LoginPage'
import { RequireAuth } from './features/auth/RequireAuth'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { BrokerFormPage } from './features/brokers/BrokerFormPage'
import { BrokersPage } from './features/brokers/BrokersPage'
import { CarrierDetailPage } from './features/carriers/CarrierDetailPage'
import { CarrierFormPage } from './features/carriers/CarrierFormPage'
import { CarriersPage } from './features/carriers/CarriersPage'
import { DriverFormPage } from './features/carriers/DriverFormPage'
import { TrailerFormPage } from './features/carriers/TrailerFormPage'
import { TruckFormPage } from './features/carriers/TruckFormPage'
import { CommissionCalculatorPage } from './features/commissions/CommissionCalculatorPage'
import { LandingPage } from './features/landing/LandingPage'
import './i18n'
import './index.css'

const container = document.getElementById('root')
if (container === null) {
  throw new Error('Root container #root was not found in index.html.')
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* "/" is the public landing while the apex site does not exist; the
              authenticated shell lives under /dashboard (decision 2026-08-18). */}
          <Route index element={<LandingPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="dashboard" element={<DashboardPage />} />
              {/* The settings live under the list they govern: whoever is looking at
                  a paper shouting too often is one click from the box that changes
                  how early it starts. */}
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="alerts/windows" element={<AlertWindowsPage />} />
              <Route path="brokers" element={<BrokersPage />} />
              <Route path="brokers/new" element={<BrokerFormPage />} />
              <Route path="brokers/:id" element={<BrokerFormPage />} />
              {/* Unlike a broker, a carrier's id page is a DETAIL (it owns a fleet);
                  the edit form lives one step further in. */}
              <Route path="carriers" element={<CarriersPage />} />
              <Route path="carriers/new" element={<CarrierFormPage />} />
              <Route path="carriers/:id" element={<CarrierDetailPage />} />
              <Route path="carriers/:id/edit" element={<CarrierFormPage />} />
              <Route path="carriers/:id/drivers/new" element={<DriverFormPage />} />
              <Route path="carriers/:id/drivers/:driverId" element={<DriverFormPage />} />
              <Route path="carriers/:id/trucks/new" element={<TruckFormPage />} />
              <Route path="carriers/:id/trucks/:truckId" element={<TruckFormPage />} />
              <Route path="carriers/:id/trailers/new" element={<TrailerFormPage />} />
              <Route path="carriers/:id/trailers/:trailerId" element={<TrailerFormPage />} />
              <Route path="availability" element={<AvailabilityPage />} />
              <Route path="availability/new" element={<AvailabilityFormPage />} />
              <Route path="availability/:id" element={<AvailabilityFormPage />} />
              <Route path="availability/:id/matches" element={<PostingMatchesPage />} />
              <Route path="availability/:id/broadcast" element={<BroadcastPage />} />
              <Route path="availability/:id/broadcasts/:broadcastId" element={<BroadcastDetailPage />} />
              <Route path="loads" element={<LoadsPage />} />
              <Route path="loads/new" element={<LoadFormPage />} />
              <Route path="loads/paste" element={<DatPastePage />} />
              <Route path="loads/:id" element={<LoadFormPage />} />
              <Route path="loads/:id/matches" element={<LoadMatchesPage />} />
              <Route path="rate-confirmations" element={<RateConfirmationsPage />} />
              <Route path="rate-confirmations/:id" element={<RateConfirmationPage />} />
              <Route path="commissions" element={<CommissionCalculatorPage />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
